
import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable, throwError as observableThrowError } from 'rxjs';
import { map } from 'rxjs/operators';
import { CONSTANTS, Constants, ENV, Environment } from '../commons/injection.token';
import { User } from '../models/user.model';

@Injectable({ providedIn: 'root' })
export class AuthenticationService {

  constructor(
    private http: HttpClient,
    @Inject(CONSTANTS) private constants: Constants,
    @Inject(ENV) private environment: Environment,
  ) { }

  login(email: string, password: string, recaptchaToken: string): Observable<User> {
    return this.http.post('/auth', { email: email, password: password, recaptchaToken: recaptchaToken })
      .pipe(
        map((response: any) => {
          let currentUser = response;
          if (currentUser.token) {
            localStorage.setItem(this.constants['currentUser'], JSON.stringify(currentUser));
            return currentUser.user as User;
          } else {
            throw observableThrowError(currentUser.message);
          }
        })
      );
  }

  loginImpersonate(idUser: string, idCompany: string): Observable<User> {
    return this.http.post('/auth/impersonate', { idUser, idCompany })
      .pipe(
        map((response) => {
          let currentUser: any = response;
          if (currentUser && currentUser.token) {
            localStorage.setItem(this.constants['currentUser'], JSON.stringify(currentUser));
            return currentUser.user as User;
          } else {
            throw observableThrowError(currentUser.message);
          }
        })
      );
  }

  refreshJwtTokenUserLogged(): Observable<User> {
    return this.http.get('/auth/refresher/')
      .pipe(
        map((response: any) => {
          let currentUser: any = response;
          if (currentUser && currentUser.token) {
            localStorage.setItem(this.constants['currentUser'], JSON.stringify(currentUser));
            return currentUser.user as User;
          } else {
            throw observableThrowError(currentUser.message);
          }
        })
      );
  }

  /**
   * Cheap presence beat (POST /auth/heartbeat, 204): only renews the TTL of the online marker the
   * attendance distribution reads. Called on a short interval — unlike refreshJwtTokenUserLogged,
   * which hits the database and mints a new token, so it stays on its slower cadence.
   */
  heartbeatSession(): Observable<unknown> {
    return this.http.post('/auth/heartbeat', {});
  }

  logout() {
    this.evictServerSession();
    localStorage.removeItem(this.constants['currentUser']);
  }

  /**
   * Tells the API to drop the user's online marker so the attendance distribution
   * ("Atendentes Online") stops considering them right away, instead of waiting for the cache to
   * expire. Best effort by design: uses a keepalive `fetch` (not HttpClient) because every caller
   * clears the session and hard-redirects to /login immediately after, which would cancel an XHR —
   * and because a failure here must never block the logout.
   */
  private evictServerSession(): void {
    try {
      const stored = localStorage.getItem(this.constants['currentUser']);
      const token = stored ? JSON.parse(stored)?.token : null;
      if (!token) {
        return;
      }
      fetch(`${this.environment['apiUrl']}/auth/logout`, {
        method: 'POST',
        keepalive: true,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      }).catch(() => { /* best effort */ });
    } catch {
      /* best effort — logout must never be blocked by the session cleanup */
    }
  }

}
